import { ref } from 'vue';

const HEARTBEAT_INTERVAL_MS = 10000;
const HEARTBEAT_TIMEOUT_MS = 4000;

export const isConnected = ref(typeof navigator !== 'undefined' ? navigator.onLine : true);

let heartbeatTimerId = null;
let heartbeatInFlight = false;
let initialized = false;

const isDebugEnabled = () => {
  return typeof window !== 'undefined' && window.__connectivityDebug === true;
};

const logDebug = (...args) => {
  if (!isDebugEnabled()) {
    return;
  }

  console.info('[Connectivity]', ...args);
};

const dispatchConnectivityChange = (connected) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent('app:connectivity-change', {
    detail: { isConnected: connected },
  }));
};

const setConnected = (connected) => {
  const normalized = Boolean(connected);
  if (isConnected.value === normalized) {
    return;
  }

  isConnected.value = normalized;
  logDebug('state changed:', normalized ? 'online' : 'offline');
  dispatchConnectivityChange(normalized);
};

const fetchWithTimeout = async (url, timeoutMs) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      mode: 'no-cors',
      credentials: 'omit',
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const getHeartbeatUrl = () => {
  const base = window?.AppConfig?.strapiUrl || 'https://cms.brochure.urencoisotopes.com';
  return `${base}/?connectivity_check=${Date.now()}`;
};

export const checkConnectivity = async () => {
  if (typeof window === 'undefined' || heartbeatInFlight) {
    return isConnected.value;
  }

  if (navigator.onLine === false) {
    logDebug('navigator reports offline');
    setConnected(false);
    return isConnected.value;
  }

  heartbeatInFlight = true;
  const heartbeatUrl = getHeartbeatUrl();

  try {
    await fetchWithTimeout(heartbeatUrl, HEARTBEAT_TIMEOUT_MS);
    logDebug('heartbeat success', heartbeatUrl);
    setConnected(true);
  } catch {
    logDebug('heartbeat failed', heartbeatUrl);
    setConnected(false);
  } finally {
    heartbeatInFlight = false;
  }

  return isConnected.value;
};

export const initializeConnectivityMonitor = () => {
  if (typeof window === 'undefined' || initialized) {
    return;
  }

  initialized = true;
  logDebug('monitor initialized');

  window.addEventListener('online', () => {
    void checkConnectivity();
  });

  window.addEventListener('offline', () => {
    setConnected(false);
  });

  window.addEventListener('pageshow', () => {
    void checkConnectivity();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkConnectivity();
    }
  });

  heartbeatTimerId = window.setInterval(() => {
    void checkConnectivity();
  }, HEARTBEAT_INTERVAL_MS);

  void checkConnectivity();
};

export const stopConnectivityMonitor = () => {
  if (!heartbeatTimerId) {
    return;
  }

  window.clearInterval(heartbeatTimerId);
  heartbeatTimerId = null;
};
